import { addAlert, isAuthorized } from '../../utilities/helpers'
import { confirmPassengerVerification, requestPassengerVerification } from '../../services'
import { C_SET_PASSENGER_TOKEN } from '../../utilities/mutation-types'
import { getAgent } from '../../services/agent-service'
import { decodeHoneyCommQrCodeString, decodeMinewQrCodeString, decodeKkmQrCodeString, decodeRfidQrCodeString } from '@smarttransit/common'

const SUPER_AGENT_WHITELIST = [
  'fa9b556f-ecbf-475d-b6ea-5dbf77e6e769'
]
const VERIFICATION_TIMEOUT_DURATION = 10000 // 10 seconds in milliseconds
const VERIFICATION_TIMEOUTS_STORAGE_KEY = 'passengerVerificationTimeouts'

export default {
  props: {
    childProps: Object,
    defaultPrefix: String,
    value: Object,
    initialVerificationType: Number,
    agentProfileId: String,
    onPassengerVerified: Function,
    onDetectedQrCodeChanged: Function,
    onPassengerMobileChanged: Function,
    hideVerificationOptions: Boolean,
    disableVerificationOptions: Boolean
  },
  data () {
    return {
      accessedPassenger: null,
      verificationType: 0, // 0 = "phone", 1 = "alternate id"
      passengerMobile: '',
      verificationCode: '',
      detectedQrCode: '',
      isVerifying: false,
      isSuperAgent: false,
      canRetrievePassengerVerificationCode: false,
      skipVerification: false,
      isSendingVerificationCode: false,
      countdownTimer: null,
      currentTime: Date.now() // Reactive property for countdown updates
    }
  },
  computed: {
    isVerificationDisabled () {
      // Check if button should be disabled due to missing phone number
      if (!this.passengerMobile) {
        return true
      }

      // Check if button should be disabled due to ongoing request
      if (this.isSendingVerificationCode) {
        return true
      }

      // Check if button should be disabled due to timeout for this phone number
      return this.isPhoneNumberInTimeout(this.passengerMobile)
    },
    verificationButtonText () {
      if (!this.passengerMobile) {
        return 'Send verification code'
      }

      if (this.isSendingVerificationCode) {
        return 'Send verification code'
      }

      const remainingSeconds = this.getRemainingTimeoutSeconds(this.passengerMobile)
      if (remainingSeconds > 0) {
        return `Button available again in (${remainingSeconds}${remainingSeconds === 1 ? ' second' : ' seconds'})`
      }

      return 'Send verification code'
    }
  },
  mounted () {
    if (typeof this.$props.initialVerificationType === 'number') {
      this.verificationType = this.$props.initialVerificationType
    }

    this.setPassengerVerificationPermissions(this.$props.agentProfileId)

    // Clean up expired timeouts on component mount
    this.cleanExpiredTimeouts()

    // Start countdown timer
    this.startCountdownTimer()
  },
  beforeDestroy () {
    // Clean up timer when component is destroyed
    this.stopCountdownTimer()
  },
  watch: {
    passengerMobile (newVal, oldVal) {
      if (typeof this.$props.onPassengerMobileChanged === 'function') {
        this.$props.onPassengerMobileChanged(newVal)

        if (typeof this.$props.onDetectedQrCodeChanged === 'function') {
          this.$props.onDetectedQrCodeChanged('')
        }
      }

      // Update current time immediately when phone number changes
      if (newVal !== oldVal) {
        this.currentTime = Date.now()
      }
    },
    agentProfileId (newVal) {
      this.setPassengerVerificationPermissions(newVal)
    },
    accessedPassenger (val) {
      this.$emit('input', val)
    },
    detectedQrCode (val) {
      if (val) {
        if (!this.$props.disableVerificationOptions) {
          this.sendVerificationCode()
        }

        if (typeof this.$props.onDetectedQrCodeChanged === 'function') {
          this.$props.onDetectedQrCodeChanged(val)

          if (typeof this.$props.onPassengerMobileChanged === 'function') {
            this.$props.onPassengerMobileChanged('')
          }
        }
      }
    }
  },
  methods: {
    filterQrCode (qrcodeData) {
      if (!qrcodeData || typeof qrcodeData !== 'string') {
        return ''
      }

      return decodeHoneyCommQrCodeString(qrcodeData) ||
        decodeMinewQrCodeString(qrcodeData) ||
        decodeKkmQrCodeString(qrcodeData) ||
        decodeRfidQrCodeString(qrcodeData) ||
        ''
    },
    setPassengerVerificationPermissions (agentProfileId) {
      this.isSuperAgent = SUPER_AGENT_WHITELIST.indexOf(agentProfileId) > -1
      this.canRetrievePassengerVerificationCode = this.isSuperAgent
      if (!this.canRetrievePassengerVerificationCode) {
        this.skipVerification = false
      }

      if (!agentProfileId) {
        return
      }

      getAgent({ id: agentProfileId }).then((agentProfile) => {
        const canRetrievePassengerVerificationCode = this.isSuperAgent || isAuthorized(agentProfile.stUser, 'admin')
        this.canRetrievePassengerVerificationCode = canRetrievePassengerVerificationCode
        if (!canRetrievePassengerVerificationCode) {
          this.skipVerification = false
        }
      }).catch((error) => {
        if (!this.isSuperAgent) {
          addAlert({ message: error, type: 'error' })
        }
      })
    },
    startCountdownTimer () {
      // Update countdown every second by updating the reactive currentTime property
      this.countdownTimer = setInterval(() => {
        this.currentTime = Date.now()
      }, 1000)
    },
    stopCountdownTimer () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    restartCountdownTimer () {
      this.stopCountdownTimer()
      this.startCountdownTimer()
    },
    getVerificationTimeouts () {
      try {
        const timeouts = localStorage.getItem(VERIFICATION_TIMEOUTS_STORAGE_KEY)
        return timeouts ? JSON.parse(timeouts) : {}
      } catch (error) {
        console.warn('Failed to parse verification timeouts from localStorage:', error)
        return {}
      }
    },
    setVerificationTimeout (phoneNumber) {
      try {
        const timeouts = this.getVerificationTimeouts()
        const expirationTime = Date.now() + VERIFICATION_TIMEOUT_DURATION
        timeouts[phoneNumber] = expirationTime
        localStorage.setItem(VERIFICATION_TIMEOUTS_STORAGE_KEY, JSON.stringify(timeouts))

        // Update current time to trigger reactivity immediately
        this.currentTime = Date.now()
      } catch (error) {
        console.warn('Failed to set verification timeout in localStorage:', error)
      }
    },
    isPhoneNumberInTimeout (phoneNumber) {
      const timeouts = this.getVerificationTimeouts()
      const expirationTime = timeouts[phoneNumber]

      if (!expirationTime) {
        return false
      }

      // Use reactive currentTime instead of Date.now() directly
      const isExpired = this.currentTime >= expirationTime

      if (isExpired) {
        // Clean up this expired timeout
        this.removeVerificationTimeout(phoneNumber)
        return false
      }

      return true
    },
    getRemainingTimeoutSeconds (phoneNumber) {
      const timeouts = this.getVerificationTimeouts()
      const expirationTime = timeouts[phoneNumber]

      if (!expirationTime) {
        return 0
      }

      // Use reactive currentTime instead of Date.now() directly
      const remainingMs = expirationTime - this.currentTime

      if (remainingMs <= 0) {
        // Clean up this expired timeout
        this.removeVerificationTimeout(phoneNumber)
        return 0
      }

      return Math.ceil(remainingMs / 1000)
    },
    removeVerificationTimeout (phoneNumber) {
      try {
        const timeouts = this.getVerificationTimeouts()
        delete timeouts[phoneNumber]
        localStorage.setItem(VERIFICATION_TIMEOUTS_STORAGE_KEY, JSON.stringify(timeouts))
      } catch (error) {
        console.warn('Failed to remove verification timeout from localStorage:', error)
      }
    },
    cleanExpiredTimeouts () {
      try {
        const timeouts = this.getVerificationTimeouts()
        const currentTime = Date.now()
        let hasExpiredTimeouts = false

        Object.keys(timeouts).forEach(phoneNumber => {
          if (currentTime >= timeouts[phoneNumber]) {
            delete timeouts[phoneNumber]
            hasExpiredTimeouts = true
          }
        })

        if (hasExpiredTimeouts) {
          localStorage.setItem(VERIFICATION_TIMEOUTS_STORAGE_KEY, JSON.stringify(timeouts))
        }
      } catch (error) {
        console.warn('Failed to clean expired timeouts from localStorage:', error)
      }
    },
    async sendVerificationCode () {
      try {
        this.isSendingVerificationCode = true

        const result = await requestPassengerVerification({
          agentProfileId: this.$props.agentProfileId,
          phoneNumber: this.verificationType === 0 ? this.passengerMobile : undefined,
          alternateId: this.verificationType === 1 ? this.detectedQrCode : undefined,
          skipVerification: this.canRetrievePassengerVerificationCode && this.skipVerification
        })

        // Set timeout for this phone number after successful request
        if (this.verificationType === 0 && this.passengerMobile) {
          this.setVerificationTimeout(this.passengerMobile)
        }

        if (this.canRetrievePassengerVerificationCode && this.skipVerification) {
          this.verificationCode = result && result.verificationCode ? result.verificationCode : ''
        } else {
          addAlert({ message: 'Verification request successfully sent, please ask passenger for the text sent to their mobile device', type: 'success' })
        }
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      } finally {
        this.isSendingVerificationCode = false
      }
    },
    async confirmVerification () {
      try {
        this.isVerifying = true

        const result = await confirmPassengerVerification({
          agentProfileId: this.$props.agentProfileId,
          phoneNumber: this.verificationType === 0 ? this.passengerMobile : undefined,
          alternateId: this.verificationType === 1 ? this.detectedQrCode : undefined,
          code: this.verificationCode
        })

        this.$store.commit(C_SET_PASSENGER_TOKEN, { newPassengerToken: result })

        if (typeof this.$props.onPassengerVerified === 'function') {
          this.$props.onPassengerVerified(result)
        }
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      } finally {
        this.isVerifying = false
      }
    },
    resetQrcodeScan () {
      this.detectedQrCode = ''
    }
  }
}
